import { useEffect, useState } from "react";
import { getProducts, checkout } from "@/lib/api";
import { Overline, PrimaryButton } from "@/components/site/Primitives";
import { ProductCard } from "@/components/site/ProductCard";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { NewsletterSection } from "@/components/site/Newsletter";
import { Reveal } from "@/lib/reveal";
import { IMAGES } from "@/lib/constants";
import { toast } from "sonner";

export default function Library() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [loadingBundle, setLoadingBundle] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => { getProducts().then(setProducts).catch(() => {}); }, []);

  const cats = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const shown = cat === "All" ? products : products.filter((p) => p.category === cat);
  const total = products.reduce((s, p) => s + p.price, 0);
  const bundlePrice = 79;

  const doCheckout = async (name, email) => {
    setLoadingBundle(true);
    try {
      const res = await checkout({ lookup_key: "cottage-library-bundle", origin_url: window.location.origin, name, email });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open checkout. Please try again."); setLoadingBundle(false); }
  };

  return (
    <div data-testid="library-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-12 text-center">
        <Overline>Premium Digital Editions</Overline>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light mt-5 mb-6 leading-none">The Cottage Library</h1>
        <p className="font-body text-brand-body text-lg leading-relaxed max-w-2xl mx-auto">
          A collection of beautifully made digital books and journals — each one a keepsake, gathered over a lifetime
          and set down with care for your own home.
        </p>
      </section>

      <div className="flex flex-wrap justify-center gap-3 px-6 mb-16">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c}`}
            onClick={() => setCat(c)}
            className={`font-sans text-[0.72rem] uppercase tracking-[0.14em] px-5 py-2 border transition-colors duration-300 ${
              cat === c ? "bg-brand-olive text-brand-cream border-brand-olive" : "border-brand-linen text-brand-body hover:border-brand-gold"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {shown.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}><ProductCard product={p} /></Reveal>
          ))}
        </div>
      </section>

      {/* WHOLE LIBRARY BUNDLE */}
      {products.length > 1 && (
        <section className="relative" data-testid="bundle-section">
          <img src={IMAGES.hero} alt="The whole cottage library" className="w-full h-[70vh] object-cover" />
          <div className="absolute inset-0 bg-brand-charcoal/55" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
              <div className="max-w-xl">
                <Overline className="text-brand-gold">The complete collection</Overline>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-brand-cream mt-4 mb-5 leading-tight">The Whole Cottage Library</h2>
                <p className="font-body text-brand-cream/90 text-lg leading-relaxed mb-6">
                  Every book and journal I've ever written, gathered into one collection — a lifetime of recipes,
                  remedies and homemaking wisdom, yours to keep forever.
                </p>
                <div className="flex items-baseline gap-4 mb-8">
                  <span className="font-serif text-5xl text-brand-cream">${bundlePrice}</span>
                  {total > bundlePrice && (
                    <>
                      <span className="font-body text-xl text-brand-cream/60 line-through">${total.toFixed(0)}</span>
                      <span className="font-sans text-xs uppercase tracking-[0.16em] text-brand-gold">Save ${Math.round(total - bundlePrice)}</span>
                    </>
                  )}
                </div>
                <PrimaryButton onClick={() => setCheckoutOpen(true)} testid="buy-bundle-btn" disabled={loadingBundle}>
                  {loadingBundle ? "Opening checkout…" : "Bring the whole library home"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </section>
      )}

      <NewsletterSection />
      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} onConfirm={doCheckout} submitting={loadingBundle} title="Buy the whole library" />
    </div>
  );
}
